import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, BookOpen, User, Hash, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const UploadCompletionModal = ({ isOpen, onClose, manuscriptData, enrichmentStatus = 'none' }) => {
  const [sendingEmails, setSendingEmails] = useState(false);
  
  if (!manuscriptData) return null;
  
  const handleGotIt = async () => {
    // NOTE: Emails are now sent immediately after ingestion (before outline editor)
    // This modal is just for showing completion status after TTS generation
    // No need to send emails again here
    console.log('[COMPLETION] Modal closed - emails were already sent after ingestion');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <DialogTitle className="text-2xl">Your book is ready in your Author Library!</DialogTitle>
          </div>
          <DialogDescription>
            Your manuscript has been successfully uploaded and is ready to use.
            {(enrichmentStatus === 'running' || enrichmentStatus === 'pending') && (
              <span className="block mt-2 text-amber-600 dark:text-amber-400">
                We're still running additional background processing (AI enhancements, summaries, etc.). 
                You can start using your book now — these enhancements will continue in the background.
              </span>
            )}
            {enrichmentStatus === 'error' && (
              <span className="block mt-2 text-orange-600 dark:text-orange-400">
                Your book is ready, but some background enhancements failed. 
                Core reading and navigation will still work normally.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Manuscript Details */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Title</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {manuscriptData.title}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Author</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {manuscriptData.author}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Hash className="h-5 w-5 text-green-600 dark:text-green-400 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Chapters</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {manuscriptData.chapter_count}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Word Count</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {manuscriptData.word_count?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Notice */}
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                  Upload & Processing Complete!
                </h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  Your book has been successfully uploaded and processed. The outline is ready for editing.
                </p>
              </div>
            </div>
          </div>

          {/* API Cost Summary */}
          {manuscriptData.api_cost_total !== undefined && manuscriptData.api_cost_total > 0 && (
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Processing Costs
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    AI Analysis (Claude): <span className="font-bold">${manuscriptData.api_cost_total.toFixed(4)}</span>
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                    TTS audio generation costs will be added when you generate audio
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button 
            onClick={handleGotIt} 
            size="lg" 
            className="px-8"
            disabled={sendingEmails}
          >
            {sendingEmails ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending Emails...
              </>
            ) : (
              'Ok, Got it!'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadCompletionModal;
